import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventClassResponse, EventResponse, EventScheduleResponse, EventScheduleEntryResponse, EventScheduleGroupResponse } from '../../../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import { _AQH_EVE_41, _AQH_EVE_46, _AQH_EVE_73, AQH_EVE_6, AQH_EVE_44 } from '../../../../../../../../../../@shared/models/input.module';
import { firstValueFrom } from 'rxjs';

interface TargetOption {
  eventClass: EventClassResponse;
  dayLabel: string;
  isSameClass: boolean;
  isSameDay: boolean;
}

interface DayGroup {
  dayLabel: string;
  date: Date;
  isSameDay: boolean;
  options: TargetOption[];
}

@Component({
  selector: 'app-copy-schedule-modal',
  standalone: false,
  templateUrl: './copy-schedule-modal.html',
  styleUrl: './copy-schedule-modal.css'
})
export class CopyScheduleModal {
  @Input() schedule: EventScheduleResponse;
  @Input() eventClass: EventClassResponse;
  @Input() event: EventResponse;

  allClasses: EventClassResponse[] = [];
  targetOptions: TargetOption[] = [];
  dayGroups: DayGroup[] = [];
  selectedTargets: TargetOption[] = [];

  loading = true;
  copying = false;
  error: string | null = null;

  constructor(
    private modal: NgbActiveModal,
    private eventService: EventsService
  ) {}

  ngOnInit() {
    this.loadAllClasses();
  }

  loadAllClasses() {
    const params: AQH_EVE_6 = {
      event_id: this.event.event_id
    };

    this.eventService.getEventClasses(params).subscribe({
      next: (classes) => {
        this.allClasses = classes || [];
        this.buildTargetOptions();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'Failed to load classes';
      }
    });
  }

  buildTargetOptions() {
    this.targetOptions = [];
    this.dayGroups = [];

    const currentClassDate = this.eventClass.start_date_time
      ? new Date(this.eventClass.start_date_time)
      : null;

    // Build target options
    for (const cls of this.allClasses) {
      if (!cls.start_date_time) continue;

      const classDate = new Date(cls.start_date_time);
      const isSameClass = cls.event_class_id === this.eventClass.event_class_id;
      const isSameDay = currentClassDate ? this.isSameDay(classDate, currentClassDate) : false;

      this.targetOptions.push({
        eventClass: cls,
        dayLabel: this.formatDayLabel(classDate),
        isSameClass,
        isSameDay
      });
    }

    // Group by day
    const dayMap = new Map<string, DayGroup>();

    for (const option of this.targetOptions) {
      const classDate = new Date(option.eventClass.start_date_time!);
      const dateKey = classDate.toISOString().split('T')[0];

      if (!dayMap.has(dateKey)) {
        dayMap.set(dateKey, {
          dayLabel: option.dayLabel,
          date: classDate,
          isSameDay: option.isSameDay,
          options: []
        });
      }

      dayMap.get(dateKey)!.options.push(option);
    }

    // Convert to array and sort by date (same day first)
    this.dayGroups = Array.from(dayMap.values()).sort((a, b) => {
      if (a.isSameDay && !b.isSameDay) return -1;
      if (!a.isSameDay && b.isSameDay) return 1;
      return a.date.getTime() - b.date.getTime();
    });

    // Sort classes within each day (same class first)
    for (const group of this.dayGroups) {
      group.options.sort((a, b) => {
        if (a.isSameClass && !b.isSameClass) return -1;
        if (!a.isSameClass && b.isSameClass) return 1;
        return 0;
      });
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' }).toLowerCase();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} - ${day}.${month}.${year}`;
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  toggleTarget(option: TargetOption) {
    const index = this.selectedTargets.findIndex(t =>
      t.eventClass.event_class_id === option.eventClass.event_class_id
    );

    if (index >= 0) {
      this.selectedTargets.splice(index, 1);
    } else {
      this.selectedTargets.push(option);
    }
  }

  isTargetSelected(option: TargetOption): boolean {
    return this.selectedTargets.some(t =>
      t.eventClass.event_class_id === option.eventClass.event_class_id
    );
  }

  getTargetLabel(option: TargetOption): string {
    let label = option.eventClass.class_name;

    if (option.isSameClass && option.isSameDay) {
      label += ' (same class, same day)';
    } else if (option.isSameClass) {
      label += ' (same class)';
    } else if (option.isSameDay) {
      label += ' (same day)';
    }

    return label;
  }

  async copySchedule() {
    if (this.selectedTargets.length === 0) return;

    this.copying = true;
    this.error = null;

    try {
      // For group sessions, load the entries and groups
      const isGroupSession = this.schedule.session_type === 'group';
      let originalEntries: EventScheduleEntryResponse[] = [];
      let originalGroups: EventScheduleGroupResponse[] = [];

      if (isGroupSession) {
        // Load entries from the API
        const entriesParams: AQH_EVE_44 = {
          schedule_id: this.schedule.schedule_id
        };
        originalEntries = await firstValueFrom(this.eventService.getScheduleEntries(entriesParams));

        // Load groups from the API (they might not be in schedule object)
        originalGroups = await firstValueFrom(this.eventService.getScheduleGroups(this.schedule.schedule_id));
      }

      for (const target of this.selectedTargets) {
        // Determine schedule name
        let scheduleName = this.schedule.schedule_name || 'Schedule';
        if (target.isSameClass) {
          scheduleName += ' (copy)';
        }

        // Calculate new start time if schedule has one
        let newStartTime: string | undefined;
        if (this.schedule.schedule_start_time) {
          const originalStart = new Date(this.schedule.schedule_start_time);
          const targetClassDate = new Date(target.eventClass.start_date_time!);

          // Keep the same time, but change the date
          const newStart = new Date(targetClassDate);
          newStart.setHours(originalStart.getHours(), originalStart.getMinutes(), 0, 0);
          newStartTime = this.formatDateTimeLocal(newStart);
        }

        const payload: _AQH_EVE_41 = {
          event_class_id: target.eventClass.event_class_id,
          schedule_name: scheduleName,
          schedule_type: this.schedule.schedule_type,
          session_type: this.schedule.session_type,
          max_group_size: this.schedule.max_group_size,
          time_per_entry_seconds: this.schedule.time_per_entry_seconds,
          default_break_seconds: this.schedule.default_break_seconds,
          schedule_start_time: newStartTime,
          auto_generate: false // Don't auto-generate entries
        };

        const newSchedule = await firstValueFrom(this.eventService.createSchedule(payload));

        // For group sessions, copy groups and breaks (without participants)
        if (isGroupSession && newSchedule && (originalGroups.length > 0 || originalEntries.length > 0)) {
          await this.copyGroupsAndBreaks(newSchedule.schedule_id, originalEntries, originalGroups);
        }
      }

      this.copying = false;
      this.modal.close(true);
    } catch (error) {
      console.error('Error copying schedule:', error);
      this.error = 'Failed to copy schedule. Please try again.';
      this.copying = false;
    }
  }

  async copyGroupsAndBreaks(
    newScheduleId: number,
    originalEntries: EventScheduleEntryResponse[],
    originalGroups: EventScheduleGroupResponse[]
  ) {
    // Get breaks from entries
    const breaks = originalEntries.filter(entry => entry.entry_type === 'break');

    console.log('Copying groups:', originalGroups.length, 'breaks:', breaks.length);

    // Copy groups using the new API
    let groupPosition = 1;
    for (const group of originalGroups) {
      const groupPayload: _AQH_EVE_73 = {
        schedule_id: newScheduleId,
        group_name: group.group_name,
        position: groupPosition++,
        start_time: group.start_time ? new Date(group.start_time).toISOString() : undefined,
        end_time: group.end_time ? new Date(group.end_time).toISOString() : undefined,
        duration_seconds: group.duration_seconds || undefined,
        max_participants: group.max_participants || undefined
      };

      console.log('Creating group:', groupPayload);
      await firstValueFrom(this.eventService.createScheduleGroup(groupPayload));
    }

    // Create breaks
    let breakPosition = 1;
    for (const breakEntry of breaks) {
      const entryPayload: _AQH_EVE_46 = {
        schedule_id: newScheduleId,
        position: breakPosition++,
        entry_type: 'break',
        duration_seconds: breakEntry.duration_seconds || undefined,
        notes: breakEntry.notes || undefined
      };

      console.log('Creating break:', entryPayload);
      await firstValueFrom(this.eventService.addScheduleEntry(entryPayload));
    }
  }

  formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  close() {
    this.modal.close(false);
  }
}
